import { useState, useEffect, useRef } from 'react';
import {Row, Col, Form, Button, Collapse} from 'react-bootstrap';
import EditAdminForm from '../forms/EditAdminForm';
import EditUserForm from '../forms/EditUserForm';
import EditCondominiumForm from '../forms/EditCondominiumForm';
import EditTechnicalInterventionForm from '../forms/EditTechnicalInterventionForm'
import { iminioBackend } from '../../api/iminio_provider';
import IminioButton from '../iminio_comps/buttons/IminioButton';
import IminioUserListItem from '../iminio_comps/lists/IminioUserListItem'
import IminioCondominiumListItem from '../iminio_comps/lists/IminioCondominiumListItem'
import IminioTechnicalInterventionListItem from '../iminio_comps/lists/IminioTechnicalInterventionListItem'
import IminioDataListHeader from './IminioDataListHeader';
import i18n from '../../plugins/i18n';

let quickSearchInterval = null
let refreshHeadersInterval = null
let showItemsInterval = null

function IminioDataList({ userGroup, userId, modelName, changeView }) {

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingNext, setIsLoadingNext] = useState(false);
    const [isLoadingHeaders, setIsLoadingHeaders] = useState(true);
    const [isLoadingFilters, setIsLoadingFilters] = useState(true);
    const [currPage, setCurrPage] = useState(0);
    const [hasNext, setHasNext] = useState(true);
    const [showFilter, setShowFilter] = useState(false);
    const [filtersForModel, setFiltersForModel] = useState([]);
    const [filterObj, setFilterObj] = useState({});
    const [contentHeaders, setContentHeaders] = useState([]);
    const [activeHeader, setActiveHeader] = useState(null);
    const [listContent, setListContent] = useState(null);
    const [currFilterField, setCurrFilterField] = useState([]);
    const [currTechnicianList, setCurrTechnicianField] = useState('own');
    const [viewClosed, setViewClosed] = useState(false);

    // const filterFieldRef = useRef('0')
    const filterValueRef = useRef("")

    useEffect(() => {
      const updateWindowDimensions = () => {
        const newWidth = window.innerWidth;
        setWindowWidth(newWidth);
        clearTimeout(refreshHeadersInterval)
        refreshHeadersInterval = setTimeout(()=>{
            setIsLoadingHeaders(true)
        }, 600)
      };
      window.addEventListener("resize", updateWindowDimensions);
      return () => window.removeEventListener("resize", updateWindowDimensions)
    }, []);

    const loadNextPage = async () => {
        let results = []
        switch(modelName){
            case 'user':
                results = await fetchUserList(false)
                break;
            case 'admin': 
                results = await fetchAdminList(false)
                break;
            case 'condominium':
                results = await fetchCondominiumList(false)
                break;
            case 'technical_intervention':
                results = await fetchInterventionList(false)
                break;
            default:
                return;
        }
        if(results.length ===0) setHasNext(false)
        setListContent(listContent==null?results:listContent.concat(results))
        setIsLoadingNext(false)
        setTimeout(() => {findAndShowElements() }, 500);
    }

    useEffect(() => {
        const listElement = document.getElementById('iminio-dashboard-items-container');
        if(!listElement) return
        const onScroll = async (e)=>{
            if(hasNext===false) return;
            let reachedEnd = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop) <= 1
            if(reachedEnd){
                if(isLoadingNext===false){
                    setIsLoadingNext(true)
                    setCurrPage(currPage+1)
                }
            }
        }
        listElement.addEventListener('scroll', onScroll)
        return () => listElement.removeEventListener("scroll", onScroll)
      }, [isLoading, isLoadingNext]);

    const fetchUserList = async (update_list=true) => {
        return await iminioBackend.list_users(currPage+1, filterObj).then( async response=>{
            if(update_list)
                setListContent(response.users);
            else return response.users
        }).catch(e=>console.log(e))
    }

    const fetchCondominiumList = async (update_list=true, filter=true) => {
        return await iminioBackend.list_condominia(currPage+1, filter?filterObj:{}).then(response=>{
            if(update_list)
                setListContent(response.condominia);
            else return response.condominia
        }).catch(
            e=>console.log(e)
        )
    }

    const fetchAdminList = async (update_list=true,filter=true) => {
        return await iminioBackend.list_admin_users(currPage+1, filter?filterObj:{}).then(response=>{
            if(update_list)
                setListContent(response.users);
            else return response.users
        }).catch(e=>console.log(e))
    }

    const fetchInterventionList = async (update_list=true) => {
        let func = iminioBackend.list_technical_interventions
        if(userGroup==='technician'){
            let filter = {}
            if(currTechnicianList==='own'){
                func = iminioBackend.list_own_technical_interventions
                filter = filterObj
            }
            else filter = {...filterObj, status:'closed'}
            return await func(currPage+1, filter).then(response=>{
                if(update_list)
                    setListContent(response.technical_interventions);
                else return response.technical_interventions
            }).catch(e=>console.log(e))
        }
        else{
            let filter = {}
            if(viewClosed===true)
                filter = {...filterObj, status:'closed'}
            else filter = {status:'NOT_closed', ...filterObj}
            return await func(currPage+1, filter).then(response=>{
                if(update_list)
                    setListContent(response.technical_interventions);
                else return response.technical_interventions
            }).catch(e=>console.log(e))
        }

    }

    const fetchInterventionTypes = async () => {
        return await iminioBackend.list_intervention_types({}).then(response=>{return response.types}).catch(e=>console.log(e))
    }

    const fetchSystemTypeList = async (update_list=true) => {
        return await iminioBackend.list_condominia_system_types({}).then(response=>{return response.system_types}).catch(e=>console.log(e))
    }

    const assignHeaders = async () => {
        switch(modelName){
            case 'user':
            case 'admin':
                if(isLoadingHeaders===true)
                    setContentHeaders([
                        {key: 'full_name', text: i18n.t('models.user.fields.full_name'), xs:4, sm:3,  md:2, lg:3},
                        {key: 'email', text: i18n.t('models.user.fields.email'), xs:4, sm:4, md:3, lg:3,},
                        windowWidth<900? {} : {key: 'phone', text: i18n.t('models.user.fields.phone'), xs:0, sm: 0, md:2, p:2},
                    ])
                break;
            case 'condominium':
                if(isLoadingHeaders===true)
                    setContentHeaders([
                        {key: 'name', text: i18n.t('models.condominium.fields.name'), xs:5, md:3, min: "200px"},
                    ])
                break;
            case 'technical_intervention':
                if(isLoadingHeaders===true){
                    if(windowWidth<900)
                        setContentHeaders([
                            {key: 'condominium_id', text: i18n.t('models.technical_intervention.fields.condominium'), xs:3, sm:3, md:2},
                            {key: 'type_id', text: i18n.t('models.technical_intervention.fields.type'), xs:4, sm:4, md:3, lg:3,},
                            {key: 'activity_at', text: i18n.t('models.technical_intervention.fields.date'), xs:1, md:1, lg:1, min:'80px', max:'80px'},
                        ])
                    else
                        setContentHeaders([
                            {key: 'condominium_id', text: i18n.t('models.technical_intervention.fields.condominium'), xs:3, md:2, min: "172px"},
                            {key: 'type_id', text: i18n.t('models.technical_intervention.fields.type'), xs:2, md:3, lg:3, min: '90px'},
                            {key: 'activity_at', text: i18n.t('models.technical_intervention.fields.date'), xs:1, md:1, lg:1},
                            {key: 'status', text: i18n.t('models.technical_intervention.fields.status'), xs:2, md:1, p:2, max:"120px", min:"110px"},
                            {key: 'category_id', text: i18n.t('models.technical_intervention.fields.category'), xs:1, md:1, lg:1, p:0},
                            {key: 'assigned_to_id', text: i18n.t('models.technical_intervention.fields.assigned_to_id'), xs:2, md:2, p:0},
                        ])
                }
                break;
            default: break;
        }
        setIsLoadingHeaders(false)
    }

    const reloadList = () => {
        if(currPage === 0)
            fetchResults()
        else setCurrPage(0)
    }

    useEffect(()=>{
        if(currPage===0)
            fetchResults()
        else loadNextPage(currPage+1)
    }, [currPage])

    const fetchResults = async () => {
        setHasNext(true)
        setCurrPage(0)
        setIsLoading(true)
        setListContent(null)
        setTimeout(async () => {     
            await assignHeaders()
            switch(modelName){
                case 'user':
                    await fetchUserList()
                    break;
                case 'admin': 
                    await fetchAdminList()
                    break;
                case 'condominium':
                    await fetchCondominiumList()
                    break;
                case 'technical_intervention':
                    await fetchInterventionList()
                    break;
                default: break;
            }
            setTimeout(() => {setIsLoading(false) }, 120);
        }, 300);
    }

    const changeViewToCreate = (modelName) => {
        switch(modelName){
            case 'user':
                changeView('create-user-form')
                break;
            case 'admin':
                changeView('create-admin-form')
                break;
            case 'condominium':
                changeView('create-condominium-form')
                break;
            case 'technical_intervention':
                changeView('create-intervention-form')
                break;
            default: break;
        }
    }

    const getFieldsForFilter = async (model) => {
        let systemTypes = []
        switch(model){
            case 'user':
            case 'admin':
                setFiltersForModel([
                    {key: 'full_name', text: i18n.t('models.user.fields.full_name'), type: "text"},
                    {key: 'email', text: i18n.t('models.user.fields.email'), type: "text"},
                    {key: 'phone', text: i18n.t('models.user.fields.phone'), type: "text"},
                ]);
                setIsLoadingFilters(false)
                break;
            case 'condominium':
                systemTypes = await fetchSystemTypeList()
                let condFilters = [
                    {key: 'name', text: i18n.t('models.condominium.fields.name'), type:"text"},
                    {key: 'contact_person_name', text: i18n.t('models.condominium.fields.contact_person_name'), type:"text"},
                    {
                        key: 'system_types', 
                        text: i18n.t('models.condominium.fields.system_type_id'), 
                        type:"choice", 
                        choices: systemTypes.map(e=>{return {value: e.id, text: i18n.t(`models.condominium.system_type.${e.codename}`)}})
                    },
                ]
                if(userGroup==='admin'){
                    const admins = await fetchAdminList(false, false)
                    condFilters.push({
                        key: 'managed_by_id', 
                        text: i18n.t('models.condominium.fields.managed_by_id'), 
                        type:"choice", 
                        choices: admins?.map(e=>{return {value: e.id, text: `${e.first_name} ${e.last_name}`}})
                    })
                }
                setFiltersForModel(condFilters)
                setIsLoadingFilters(false)
                break;
            case 'technical_intervention':
                let interventionTypes = await fetchInterventionTypes()
                const userOptions = await iminioBackend.list_user_options().then(r=>{return r.users})
                let condominiumList = []
                try {
                    const condominiumResponse = await iminioBackend.list_all_condominia()
                    // Gestisce sia il caso in cui la risposta sia già parsata che quando è una stringa
                    if (typeof condominiumResponse === 'string') {
                        condominiumList = JSON.parse(condominiumResponse).condominia || []
                    } else {
                        condominiumList = condominiumResponse?.condominia || condominiumResponse || []
                    }
                    console.log('Condomini caricati per filtro:', condominiumList.length, condominiumList)
                } catch (e) {
                    console.error('Errore nel caricamento condomini:', e)
                    condominiumList = []
                }
                const interventionCategoryList = await iminioBackend.list_intervention_categories()
                let interventionFilters = [
                    {
                        key: 'type_id',
                        text: i18n.t("models.technical_intervention.fields.type"),
                        type:'choice',
                        choices: interventionTypes.map(e=>{return {value: e.id, text: i18n.t(`models.technical_intervention.types.${e.codename}`)}})
                    },
                    {
                        key: 'condominium_id',
                        text: i18n.t("models.technical_intervention.fields.condominium"),
                        type:'choice',
                        choices: (condominiumList || []).map(e=>{return {value: e.id, text: e.name}})
                    },
                    {
                        key: 'category_id',
                        text: i18n.t("models.technical_intervention.fields.category"),
                        type:'choice',
                        choices: interventionCategoryList.categories.map(e=>{return{
                            id: e.id,
                            value: e.id,
                            text: i18n.t(`models.technical_intervention.category.${e.codename}`)
                        }})
                    },
                    {
                        key: 'activity_at',
                        text: i18n.t("models.technical_intervention.fields.activity_at"),
                        type:'text',
                    },
                ]
                if(userGroup==='admin'){
                    interventionFilters.push({
                        key: 'assigned_to_id',
                        text: i18n.t("models.technical_intervention.fields.assigned_to"),
                        type:'choice',
                        choices: userOptions.map(e=>{return {value: e.id, text: `${e.first_name} ${e.last_name}`}})
                    },)
                    interventionFilters.push({
                        key: 'status',
                        text: i18n.t("models.technical_intervention.fields.status"),
                        type:'choice',
                        choices: [
                            {value: "pending", text: "Da Eseguire"},
                            {value: "in_progress", text: "Preso in Carico"},
                            {value: "completed", text: "Eseguito"},
                            {value: "closed", text: "Chiuso"}
                        ]
                    },)
                }
                setFiltersForModel(interventionFilters)
                setIsLoadingFilters(false)
                break
            default: break;;
        }
    }

    const submitFilter = () => {
        if(!currFilterField) 
            return setFilterObj({})
        const filterField = filtersForModel[currFilterField]
        if(!filterField) return;
        const fieldName = filterField['key']
        let filter = {}
        filter[fieldName] = filterValueRef.current.value
        if(filter[fieldName]==null || filter[fieldName].length===0)
            setFilterObj({})
        else{
            if(modelName==='technical_intervention' && fieldName==='status'){
                setViewClosed(false)
            }
            setFilterObj(filter)
        }
    }

    const updateListItem = (data) => {
        const index = listContent?.findIndex(e=>e.id===data.id)
        if(index>=0){
            listContent[index] = data
        }
    }

    const handleHeaderClick = async (headerValue, order) => {
        setActiveHeader(headerValue)
        setFilterObj({
            ...filterObj,
            sortBy:  headerValue,
            order: order
        })
    }

    const handleTypeQuickSearch = async (event)=>{
        clearTimeout(quickSearchInterval)
        quickSearchInterval =  setTimeout(() => {
            if(event.target.value==null || event.target.value.length==0)
                return setFilterObj({})
            switch(modelName){
                case 'user':
                case 'admin':
                    setFilterObj({full_name: event.target.value})
                    break;
                case 'condominium':
                    setFilterObj({name: event.target.value})
                    break;
                default:
                    break;
            }
        }, 500); 
    }

    const handleShowDeletedClick = async (event)=>{
        setFilterObj({...filterObj, is_deleted: event.target.checked})
    }

    useEffect(()=>{
        setHasNext(true)
        setIsLoadingNext(false)
        const init = async () => {
            switch(modelName){
                case 'user':
                case 'admin':
                    setActiveHeader('full_name')
                    setFilterObj({sortBy:'full_name'})
                    break;
                case 'condominium':
                    setActiveHeader('name')
                    setFilterObj({sortBy:'name'})
                    break;
                case 'technical_intervention':
                    setActiveHeader('activity_at')
                    setFilterObj({sortBy:'activity_at'})
                    break;
                default:
                    setActiveHeader(null)
                    setFilterObj({})
            }
            setShowFilter(false)
            setIsLoadingHeaders(true)
            setContentHeaders([])
            // await reloadList()
            await setIsLoadingFilters(true)
            await getFieldsForFilter(modelName)
        }
        init()
    }, [modelName])

    const findAndShowElements = () => {
        if(isLoading===false){
            let listElements = document.getElementsByClassName('iminio-data-list-item')
            if(listElements?.length === 0 || listContent?.length > listElements?.length)
                return setTimeout(()=>{findAndShowElements()}, 50)
            setTimeout(() => {
                let listElements = document.getElementsByClassName('iminio-data-list-item')
                for (let i = 0; i < listElements.length; i++) {
                    setTimeout(() => {
                        listElements[i]?.classList.add("iminio-data-list-item-show")
                    }, 35*i);
                }
            }, 100);
        }
    }
    useEffect(()=>{
        findAndShowElements()
    }, [isLoading])

    useEffect(()=>{
        const init = async() => {
            let premadeFilter = localStorage.getItem('prefilter')
            if(premadeFilter!=null && premadeFilter!=='null'){
                setShowFilter(true) 
                setTimeout(() => {
                    let parsed = JSON.parse(premadeFilter)
                    const fieldIndex = filtersForModel.findIndex(e=>{
                        return e.key===Object.keys(parsed)[0]
                    })
                    const filterSelectElement = document.getElementById('filter-select-field')
                    const filterValueElement = document.getElementById('filter-value-field')
                    filterSelectElement.value = ''+fieldIndex
                    setCurrFilterField(fieldIndex)
                    setTimeout(() => {
                        filterValueElement.value = ''+Object.values(parsed)[0]
                        filterValueRef.current.value = Object.values(parsed)[0]
                    }, 150);
                    setFilterObj(parsed)
                }, 300);
                setTimeout(async () => {
                    localStorage.setItem('prefilter', null)
                }, 350);
            }
        }
        init()
    }, [filtersForModel])

    useEffect(()=>{
        const init = async () => {
            await reloadList()
        }
        init()
    }, [filterObj, currTechnicianList])

    useEffect(()=>{
        if(isLoadingHeaders===true)
            assignHeaders()
    }, [isLoadingHeaders])

    useEffect(()=>{
        const init = async () => {
            if(showFilter === false && Object.keys(filterObj).filter(e=>{return e!=='sortBy'&&e!=='order'}).length > 0)
                setFilterObj({})
        }
        init()
    }, [showFilter])

    useEffect(()=>{
        if(currFilterField===null || currFilterField.length===0)
            setFilterObj({})
    }, [currFilterField])

    useEffect(()=>{
        fetchResults()
    }, [viewClosed])

    return (
        <>
          <Col className="iminio-dashboard-table">
                <Row className='pb-3 justify-content-start px-0'>
                    <Col xs={12} sm={3} md={3} className='ps-3 d-flex justify-content-start align-items-center'>
                        {
                            windowWidth < 600 ?
                            <h4 className="fg-orange">{i18n.t(`models.${modelName}.plural`)}</h4>
                            :
                            <h3 className="text-16">{i18n.t(`models.${modelName}.plural`)}</h3>
                        }
                    </Col>
                    <Col xs={10} sm={5} md={5} lg={5} className={`pr-3 d-flex ${windowWidth < 600 ?'mt-3':''} ${userGroup === 'technician' && windowWidth<760?'justify-content-start':'justify-content-center'} align-content-center align-items-center`}>
                        { userGroup === 'technician' && windowWidth<760 ?
                             <Button 
                                className={`iminio-button corner-round ${showFilter?'bg-transparent':'bg-orange'} fg-text align-items-center ms-2`} 
                                color="bg-orange" 
                                variant="orange" 
                                outlined="true"
                                size="sm" 
                                onClick={()=>{
                                    setShowFilter(!showFilter)
                                    if(showFilter===true)
                                        setViewClosed(false)
                                }}
                            >
                                {showFilter ? 
                                <>
                                    <i className={`mdi mdi-filter-remove-outline fg-blue text-8 me-1`}></i>
                                    <span className="text-8 fg-blue">Cancella Filtri</span>
                                </>
                                :
                                <>
                                    <i className={`mdi mdi-filter fg-white text-8 me-1`}></i>
                                    <span className="text-8 fg-white">Attiva Filtri</span>
                                </>
                                }
                            </Button>
                        : 
                            <>
                                 <i className="mdi mdi-magnify text-14 me-1"></i>
                                <Form.Control 
                                    type="text"
                                    id="iminio-filter-field"
                                    className="iminio-border-round-force"
                                    placeholder={`Cerca ${i18n.t(`models.${modelName}.plural`)}...`} 
                                    size='sm'
                                    onChange={(e)=>{handleTypeQuickSearch(e)}}
                                />
                                <Button 
                                    className={`iminio-button corner-round ${showFilter?'bg-transparent':'bg-orange'} fg-text align-items-center ms-2`} 
                                    color="bg-orange" 
                                    variant="orange" 
                                    outlined="true"
                                    size="sm" 
                                    onClick={()=>{
                                        setShowFilter(!showFilter)
                                        if(showFilter===true)
                                            setViewClosed(false)
                                    }}
                                >
                                    {showFilter ? 
                                    <>
                                        <i className={`mdi mdi-filter-remove-outline fg-blue text-8 me-1`}></i>
                                        <span className="text-8 fg-blue">Cancella Filtri</span>
                                    </>
                                    :
                                    <>
                                        <i className={`mdi mdi-filter fg-white text-8 me-1`}></i>
                                        <span className="text-8 fg-white">Attiva Filtri</span>
                                    </>
                                    }
                                </Button>
                            </>
                        }
                    </Col>
                    <Col xs={windowWidth<576?2:3} md={4} lg={4} className={`pe-3 d-flex justify-content-end align-items-center align-content-end ${windowWidth < 600 ?'mt-3':''}`} style={{flexDirection: 'row'}}>
                        { userGroup !== 'technician' ? 
                       ''
                        : (
                            modelName==='technical_intervention' &&
                            <>
                                <div id="iminio-intervention-pill" className="me-3">
                                    <div 
                                        className={`iminio-intervention-pill-item iminio-intervention-pill-item-left ${currTechnicianList==='own'?'iminio-intervention-pill-item-active':''}`} 
                                        onClick={()=>{setCurrTechnicianField('own')}}
                                    >
                                        Miei
                                    </div>
                                    <div 
                                        className={`iminio-intervention-pill-item iminio-intervention-pill-item-right ${currTechnicianList==='all'?'iminio-intervention-pill-item-active':''}`} 
                                        onClick={()=>{setCurrTechnicianField('all')}}
                                    >
                                        Storico
                                    </div>
                                </div>
                            </>)
                        }{
                        windowWidth < 525 ? '' : <IminioButton 
                            icon="mdi mdi-sync"
                            color="bg-orange"
                            textColor="fg-white"
                            onClick={()=>{reloadList()}}
                        />}
                        <div className="px-1"></div>
                        {userGroup==='technician' && modelName!=="technical_intervention" ? '' : <IminioButton 
                            label={windowWidth>900&&userGroup==='admin'? `Creare ${i18n.t(`models.${modelName}.single`)}` : ''}
                            icon="mdi mdi-plus-circle"
                            color="bg-orange"
                            textColor="fg-white"
                            onClick={()=>changeViewToCreate(modelName)}
                        />}
                    </Col>
                </Row>
                <Collapse in={showFilter}>
                    <Row className="justify-content-between px-3">
                        <Col xs={12} md={7}>
                            <Row className="justify-content-start">
                                <Col xs={4} className="pe-2">
                                    <Form.Select id="filter-select-field" className="heighted-form-select-dropdown" onChange={e=>{setCurrFilterField(e.target.value)}} size={'sm'}>
                                        <option value="">{i18n.t('words.choose_field')}</option>
                                        {isLoadingFilters ? '' : filtersForModel?.map((e, i)=>{
                                            return <option key={`filter-${e.key}`} value={i}>{e.text}</option>
                                        })}
                                    </Form.Select>
                                </Col>
                                <Col xs={4}>
                                    {
                                        currFilterField >= 0 && filtersForModel[currFilterField]?.type === 'choice' ?
                                        <Form.Select id="filter-value-field" className="heighted-form-select-dropdown" ref={filterValueRef} size="sm">
                                            <option value="">Seleziona Opzione</option>
                                            {filtersForModel[currFilterField]?.choices?.map(e=>{
                                                return <option key={`filtersel-${e.value}`} value={e.value}>{e.text}</option>
                                            })}
                                        </Form.Select>
                                        :
                                        <Form.Control
                                            id="filter-value-field"
                                            ref={filterValueRef}
                                            type="text"
                                            size={'sm'}
                                            className="iminio-border-round-force"
                                            placeholder={`${i18n.t(`words.value_to_filter`)}...`} 
                                        />
                                    }
                                </Col>
                                <Col xs={2} md={3} className="ps-2">
                                    <IminioButton 
                                        label={i18n.t(`words.filter_short`)}
                                        color="bg-orange"
                                        textColor="fg-white"
                                        size={'sm'}
                                        onClick={()=>submitFilter()}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        {userGroup==='technician' ? '' : 
                        <Col xs={12} md={5}>
                            <Row className={`${windowWidth < 777?'mt-3':''} justify-content-between`}>
                                <Col xs={5} md={6} lg={4}>
                                {
                                modelName==='technical_intervention' && userGroup === 'admin' &&
                                <>
                                    <div id="iminio-intervention-pill" className="me-3">
                                        {viewClosed===false ? 
                                        <div 
                                            className={`iminio-intervention-pill-item iminio-intervention-pill-item-both d-flex align-items-center`} 
                                            onClick={()=>{setViewClosed(true)}}
                                            style={{border: '1px solid var(--clr-green-base)'}}
                                        >
                                            <i className="mdi mdi-folder-outline fg-green me-2 text-12"></i>
                                            <span className='text-8 fg-green'>Vissualizza Chiusi</span>
                                        </div>
                                        :
                                        <div 
                                            className={`iminio-intervention-pill-item iminio-intervention-pill-item-both bg-green d-flex align-items-center`} 
                                            onClick={()=>{setViewClosed(false)}}
                                            style={{border: '1px solid var(--clr-green-base)'}}
                                        >
                                            <i className="mdi mdi-folder-check fg-white me-2 text-12"></i>
                                            <span className='text-8 fg-white'>Nascondi Chiusi</span>
                                        </div>
                                        }
                                    </div>
                                </>}
                                </Col>
                                <Col xs={6} md={6} lg={4}>
                                    <Form.Check
                                        type="switch"
                                        id="custom-switch"
                                        label={i18n.t('words.show_deleted')}
                                        className="pt-1"
                                        onChange={e=>handleShowDeletedClick(e)}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        }
                    </Row>
                </Collapse>
                <Row id="iminio-data-list-header-container" className="px-2 mt-2 align-items-center">
                    <h6 className="fg-orange-dark me-3 text-7" style={{maxWidth: '10px'}}>ID</h6>
                    { isLoadingHeaders? '' : contentHeaders.map(e=>{
                        return <IminioDataListHeader 
                            key={`header-${e.key}`}
                            xs={e.xs}
                            sm={e.sm}
                            md={e.md}
                            lg={e.lg}
                            padding={e.p}
                            min={e.min}
                            max={e.max}
                            text={e.text}
                            active={activeHeader===e.key}
                            onChange={(order)=>{handleHeaderClick(e.key, order)}}
                        />
                    }) }
                </Row>
                <div className="divider-h bg-gray" style={{maxWidth: '99%'}}></div>
                {
                    isLoading === false && listContent != null && listContent?.length === 0 ?
                    <Row className='justify-content-center'>
                        <Col xs={10} md={6} className="d-flex justify-content-center align-items-center">
                            <i className="mdi mdi-selection-search me-2"></i>
                            <span className='py-4'>{i18n.t('messages.no_results')}</span>
                        </Col>
                    </Row>
                    : ''
                }
                <Row id="iminio-dashboard-items-container" className="mt-1 pt-1">
                    {
                        isLoading || listContent == null ?
                        (
                            <Row className='d-flex justify-content-center align-items-center'>
                                <Col xs={12} md={3} className="d-flex justify-content-start align-items-center">
                                    <i className="mdi mdi-loading text-20 fg-orange mdi-spin me-3"></i>
                                    <span>Caricamento in corso...</span>
                                </Col>
                            </Row>
                        )
                        :
                        !isLoading && !isLoadingFilters && listContent!=null && listContent?.map((e, i)=> { 
                            return (
                                <div className="iminio-data-list-item" key={`item-${i}`}>
                                    {
                                        modelName !== 'user' ? '' :
                                        <IminioUserListItem id={`useritem-${e.id}`} userData={e} key={`useritem-${e.id}`} width={windowWidth}>
                                            <EditUserForm listId={`useritem-${e.id}`} userData={e} reloadList={()=>reloadList()} onUpdate={updateListItem} width={windowWidth}/>
                                        </IminioUserListItem>
                                    }
                                    {
                                        modelName !== 'condominium' ? '' :
                                        <IminioCondominiumListItem id={`conditem-${e.id}`} condominiumData={e} key={`conditem-${e.id}`}>
                                            <EditCondominiumForm 
                                                listId={`conditem-${e.id}`} 
                                                adminList={filtersForModel?.find(e=>e.key==='managed_by_id')} 
                                                systemTypeList={filtersForModel?.find(e=>e.key==='system_types')}
                                                condData={e} 
                                                changeView={changeView}
                                                userGroup={userGroup}
                                                reloadList={()=>reloadList()}
                                            />
                                        </IminioCondominiumListItem>
                                    }
                                    {
                                        modelName !== 'admin' ? '' :
                                        <IminioUserListItem id={`adminitem-${e.id}`} userData={e} key={`adminitem-${e.id}`} width={windowWidth}>
                                            <EditAdminForm listId={`adminitem-${e.id}`} userData={e} changeView={changeView} reloadList={()=>reloadList()}/>
                                        </IminioUserListItem>
                                    }
                                    {
                                        modelName !== 'technical_intervention' ? '' :
                                        <IminioTechnicalInterventionListItem listId={`intervention-${e.id}`} interventionData={e} key={`interitem-${e.id}`}>
                                            <EditTechnicalInterventionForm 
                                                listId={`intervention-${e.id}`}
                                                interventionData={e} 
                                                userGroup={userGroup}
                                                userId={userId}
                                                userList={filtersForModel.find(e=>e.key==="assigned_to_id")}
                                                categoryList={filtersForModel.find(e=>e.key==="category_id")}
                                                reloadList={()=>reloadList()}
                                                enableEditPhotos={userGroup==='admin'||(userGroup==='technician'&&e.status!=='closed')}
                                            />
                                        </IminioTechnicalInterventionListItem>
                                    }
                                </div>
                            )
                        })
                    }
                    { hasNext===false || !listContent || listContent?.length < 20 ? '' : <div className="iminio-data-list-item" style={{minHeight:'120px'}}>
                        <Row className="justify-content-center align-content-center">
                            <Col xs={1}>
                                <i className="mdi mdi-loading mdi-spin fg-orange text-24"></i>
                            </Col>
                        </Row>
                    </div>}
                </Row>
            </Col>
        </>
      );
}

export default IminioDataList